import { Box, Tab, Tabs } from '@mui/material';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import DetailsTab from './tabs/details/DetailsTab';
import MigrationDetailDTO from '@src/adapters/dto/migration/MigrationDetailDTO';
import MigrationNoteDTO from '@src/adapters/dto/migration/MigrationNoteDTO';
import ContentsTab from './tabs/contents/ContentsTab';
import IdNameDto from '@src/adapters/dto/shared/IdNameDto';
import MigrationSuggestionDTO from '@src/adapters/dto/migration/MigrationSuggestionDTO';
import MigrationItemViewDTO from '@src/adapters/dto/migration/MigrationItemViewDTO';
import { MigrationItemSuggestionStatus } from '@src/constants/MigrationItemSuggestionStatus';
import MigrationItemUpdateDTO from '@src/adapters/dto/migration/MigrationItemUpdateDTO';

interface Props {
  migration: MigrationDetailDTO;
  suggestions: MigrationSuggestionDTO[];
  items: MigrationItemViewDTO[];
  deleteItem(itemId?: string): void;
  itemUpdated(item: MigrationItemUpdateDTO): void;
  noteUpdated(note: MigrationNoteDTO): void;
  noteDeleted(noteId: string): void;
  procedureUpdated(procedure: IdNameDto): void;
  highlightItem(suggestionId?: string): void;
  suggestionUpdate(
    suggestion: MigrationSuggestionDTO,
    status: MigrationItemSuggestionStatus,
    inventoryId?: string,
    qtyOpen?: number,
    qtyOnHand?: number,
    note?: string,
    alias?: Boolean,
    isLoan?: boolean
  ): void;
  handleItemApprovalUpdate(
    itemId: string,
    suggestionId: string | undefined,
    approved: boolean
  ): void;
  refreshSuggestions(): void;
  inventoryItemAdded(item: MigrationItemViewDTO): void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function Information({
  migration,
  suggestions,
  items,
  deleteItem,
  itemUpdated,
  noteUpdated,
  noteDeleted,
  procedureUpdated,
  highlightItem,
  suggestionUpdate,
  refreshSuggestions,
  inventoryItemAdded,
  handleItemApprovalUpdate,
}: Props) {
  const [value, setValue] = useState(0);
  const methods = useForm<MigrationDetailDTO>();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <FormProvider {...methods}>
      <Tabs
        value={value}
        onChange={handleTabChange}
        aria-label="details"
        variant="fullWidth">
        <Tab label="Contents" {...a11yProps(0)} />
        <Tab label="Details" {...a11yProps(1)} />
      </Tabs>
      <CustomTabPanel value={value} index={0}>
        <ContentsTab
          migration={migration}
          items={items}
          suggestions={suggestions}
          deleteItem={deleteItem}
          itemUpdated={itemUpdated}
          highlightItem={highlightItem}
          refreshSuggestions={refreshSuggestions}
          suggestionUpdate={suggestionUpdate}
          inventoryItemAdded={inventoryItemAdded}
          handleItemApprovalUpdate={handleItemApprovalUpdate}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <DetailsTab
          migrationDetail={migration}
          onNoteUpdated={noteUpdated}
          onNoteDeleted={noteDeleted}
          onProcedureUpdated={procedureUpdated}
        />
      </CustomTabPanel>
    </FormProvider>
  );
}

export default Information;
